import classNames from 'classnames';
import React, {MouseEvent} from 'react';

import ErrorTrianglePopover from 'notebook/tables/errors/ErrorTrianglePopover';
import {hasErrorType, LoadingError} from 'notebook/tables/excess/Util';
import {TriangleLocation, TriangleState} from 'notebook/tables/TableConstants';
import {loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

import {EvaluatedCellError} from '../excess/Evaluator';

// Re-export for backward compatibility — existing imports from this file still work.
export {TriangleLocation, TriangleState};

export type VisibleTriangleState = Exclude<TriangleState, TriangleState.VALID_NO_TRIANGLE>;

const TRIANGLE_STATUS_CLASS_NAMES: Record<VisibleTriangleState, string> = {
  [TriangleState.VALID_WITH_TRIANGLE]: 'errorTriangle--hasChanges',
  [TriangleState.INVALID]: 'errorTriangle--invalid',
  [TriangleState.PARTIALLY_VALID]: 'errorTriangle--partially-valid',
  [TriangleState.ILLEGAL]: 'errorTriangle--illegal',
  [TriangleState.OUTDATED]: 'errorTriangle--outdated',
  [TriangleState.WARNING]: 'errorTriangle--warning',
  [TriangleState.UNSUBMITTED_CHANGES]: 'errorTriangle--unsubmitted-changes',
};

const TRIANGLE_LOCATION_CLASS_NAMES: Record<TriangleLocation, string> = {
  [TriangleLocation.CELL]: 'cellValidationIndicator',
  [TriangleLocation.RUN]: 'runValidationIndicator',
  [TriangleLocation.AGGREGATE]: 'aggregateValidationIndicator',
  [TriangleLocation.FORM_FIELD]: 'formFieldValidationIndicator',
};

interface Props {
  dataTestAttrs?: DataTestAttrs;
  location: TriangleLocation;
  error: EvaluatedCellError;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  state: TriangleState;
}

function stopPropagationForMediocreItemSelect(e: MouseEvent<HTMLDivElement>): void {
  e.stopPropagation();
}

/**
 * Component for rendering a triangle in the corner of a table cell (dog ear)
 */
export default function ErrorTriangle({
  dataTestAttrs,
  location,
  error,
  onClick,
  onMouseEnter,
  onMouseLeave,
  state,
}: Props): JSX.Element | null {
  if (state === TriangleState.VALID_NO_TRIANGLE) {
    return null;
  }

  return (
    <ErrorTrianglePopover error={error} errorState={state}>
      <div
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={stopPropagationForMediocreItemSelect}
        className={classNames(
          'errorTriangle',
          TRIANGLE_LOCATION_CLASS_NAMES[location],
          TRIANGLE_STATUS_CLASS_NAMES[state]
        )}
        {...getTestProps(dataTestAttrs)}
        {...(hasErrorType(error, LoadingError.type) ? loadingIndicatorAttrs() : {})}
      />
    </ErrorTrianglePopover>
  );
}
